import { useEffect, useCallback } from "react";
import { CONSTANTS } from "../data";
import * as gameCalculations from "../utils/gameCalculations";
import type { GameState } from "../types";

// Split: Level progression, environmental updates, session management

const useLevelProgression = ({ state, dispatch, audio }) => {
  const checkLevelUp = useCallback(() => {
    const newLevel = gameCalculations.getLevelFromXp(state.xp);
    if (newLevel > state.level) {
      dispatch({ type: "LEVEL_UP", newLevel });
      audio.playSfx("discovery");
      // Check for unlocks
      const unlocks = gameCalculations.checkUnlocks({ ...state, level: newLevel });
      if (unlocks?.items) {
        Object.entries(unlocks.items).forEach(([itemId, quantity]) => {
          dispatch({
            type: "PURCHASE_ITEM",
            itemId,
            quantity,
            totalCost: 0,
          });
        });
      }
    }
  }, [state.xp, state.level, dispatch, audio, state]);

  useEffect(() => {
    checkLevelUp();
  }, [state.xp, checkLevelUp]);
};

const useEnvironmentalUpdates = ({ state, dispatch }) => {
  useEffect(() => {
    if (state.screen !== "game") return;

    const interval = setInterval(() => {
      const gameTime = Date.now() - state.environmentalState.gameStartTime;
      
      const currentDay = Math.floor(gameTime / CONSTANTS.GAME_TIME.DAY_DURATION);
      if (currentDay > state.environmentalState.dayIndex) {
        dispatch({ type: "START_NEW_DAY", dayIndex: currentDay });
      }

      if (state.legendaryUnlocks.environmentalModifiers) {
        const dayProgress =
          (gameTime % CONSTANTS.GAME_TIME.DAY_DURATION) /
          CONSTANTS.GAME_TIME.DAY_DURATION;

        let timeOfDay = "MORNING";
        if (dayProgress > CONSTANTS.GAME_TIME.PERIOD_DEFINITIONS.MORNING) {
          timeOfDay = "AFTERNOON";
        }
        if (
          dayProgress >
          CONSTANTS.GAME_TIME.PERIOD_DEFINITIONS.MORNING +
            CONSTANTS.GAME_TIME.PERIOD_DEFINITIONS.AFTERNOON
        ) {
          timeOfDay = "NIGHT";
        }
        
        const updates: any = { currentTimeOfDay: timeOfDay, dayProgress };

        // Random humidity changes
        if (Math.random() < CONSTANTS.ENVIRONMENTAL_MODIFIERS.HUMIDITY_CHANGE_CHANCE) {
          const newHumidity = Math.max(
            0,
            Math.min(
              100,
              state.environmentalState.humidity + (Math.random() - 0.5) * 20
            )
          );
          updates.humidity = newHumidity;
        }
        
        dispatch({
          type: "UPDATE_ENVIRONMENTAL_STATE",
          updates,
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [state.screen, state.legendaryUnlocks, state.environmentalState, dispatch]);
};

const useSessionManagement = ({ state, dispatch }) => {
  useEffect(() => {
    if (state.screen !== "game") return;

    const interval = setInterval(() => {
      if (state.sessionMode.type === "rush") {
        const elapsed = Date.now() - state.sessionMode.startTime;
        if (elapsed >= CONSTANTS.RUSH_MODE.duration) {
          const score = state.sessionMode.score || 0;
          dispatch({
            type: "END_SESSION",
            score: {
              score,
              served: state.sessionMode.served || 0,
              tips: state.sessionMode.tips || 0,
              combos: state.sessionMode.combosDiscovered || 0,
              legendaries: 0,
              categories: 0,
            },
          });
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [state.screen, state.sessionMode, dispatch]);
};

const useTutorialInit = ({ state, dispatch }) => {
  // Helper function to check and auto-provision tutorial ingredients
  const autoProvisionTutorialIngredients = useCallback((tutorialId: string) => {
    const tutorialDef = CONSTANTS.TUTORIALS[tutorialId];
    if (!tutorialDef) return;

    // Collect all required ingredients from tutorial steps
    const requiredIngredients = new Set<string>();
    
    tutorialDef.steps.forEach(step => {
      if (step.action) {
        if (step.action.type === 'selectItem' && step.action.itemId) {
          requiredIngredients.add(step.action.itemId);
        }
        if (step.action.type === 'brew' && step.action.ingredients) {
          step.action.ingredients.forEach(ing => requiredIngredients.add(ing));
        }
        if (step.action.type === 'serve' && step.action.orderId) {
          requiredIngredients.add(step.action.orderId);
        }
      }
      if (step.spawnOrder) {
        requiredIngredients.add(step.spawnOrder);
      }
    });

    // Check inventory and auto-provision missing ingredients
    const missingIngredients: string[] = [];
    requiredIngredients.forEach(ingredientId => {
      const currentQuantity = state.inventory[ingredientId] || 0;
      if (currentQuantity === 0) {
        // Check if this is a valid item that can be auto-provisioned
        const item = CONSTANTS.ITEMS[ingredientId];
        const recipe = Object.values(CONSTANTS.RECIPES).find(r => r.result.id === ingredientId);
        
        if (item || recipe) {
          missingIngredients.push(ingredientId);
        }
      }
    });

    // Auto-provision missing ingredients
    if (missingIngredients.length > 0) {
      missingIngredients.forEach(ingredientId => {
        dispatch({
          type: "PURCHASE_ITEM",
          itemId: ingredientId,
          quantity: CONSTANTS.TUTORIAL_AUTO_PROVISION.QUANTITY_PER_INGREDIENT,
          totalCost: 0, // Free for tutorial
        });
      });

      // Show hint about auto-provisioning
      if (CONSTANTS.TUTORIAL_AUTO_PROVISION.SHOW_NOTIFICATION) {
        dispatch({
          type: "SET_HUD_HINT",
          hint: CONSTANTS.TUTORIAL_AUTO_PROVISION.NOTIFICATION_MESSAGE.replace(
            '{count}', 
            missingIngredients.length.toString()
          )
        });
        
        setTimeout(() => {
          dispatch({ type: "CLEAR_HUD_HINT" });
        }, CONSTANTS.TUTORIAL_AUTO_PROVISION.NOTIFICATION_DURATION);
      }
    }
  }, [state.inventory, dispatch]);

  useEffect(() => {
    if (state.screen !== "game" || state.tutorialState.isTutorialActive) return;

    // Onboarding - always check first
    if (
      state.level === 1 &&
      !state.tutorialState.completedTutorials.includes("onboarding") &&
      !state.tutorialState.skippedTutorials.includes("onboarding")
    ) {
      autoProvisionTutorialIngredients("onboarding");
      dispatch({ type: "START_TUTORIAL", tutorialId: "onboarding" });
      return;
    }

    // Automatic tutorial scanning - check all available tutorials when level changes
    const availableTutorials = Object.values(CONSTANTS.TUTORIALS).filter(tutorial => {
      // Check if tutorial is available for current level
      if (state.level < tutorial.levelGate) return false;
      
      // Check if tutorial hasn't been completed or skipped
      if (state.tutorialState.completedTutorials.includes(tutorial.id)) return false;
      if (state.tutorialState.skippedTutorials.includes(tutorial.id)) return false;
      
      return true;
    });

    // Sort tutorials by level gate to prioritize lower level tutorials
    availableTutorials.sort((a, b) => a.levelGate - b.levelGate);

    // Start the first available tutorial
    if (availableTutorials.length > 0) {
      const nextTutorial = availableTutorials[0];
      autoProvisionTutorialIngredients(nextTutorial.id);
      dispatch({ type: "START_TUTORIAL", tutorialId: nextTutorial.id });
      return;
    }

    // Special case: Seasonal tutorial (only if season is active)
    const seasonalTutorialDef = CONSTANTS.TUTORIALS.seasonal_drinks;
    if (
      seasonalTutorialDef &&
      state.level >= seasonalTutorialDef.levelGate &&
      state.activeSeason &&
      !state.tutorialState.completedTutorials.includes("seasonal_drinks") &&
      !state.tutorialState.skippedTutorials.includes("seasonal_drinks")
    ) {
      autoProvisionTutorialIngredients("seasonal_drinks");
      dispatch({ type: "START_TUTORIAL", tutorialId: "seasonal_drinks" });
      return;
    }
  }, [state.screen, state.level, state.tutorialState, state.activeSeason, state.inventory, dispatch, autoProvisionTutorialIngredients]);
};

const useTutorialEffects = ({ state, dispatch }) => {
  useEffect(() => {
    if (state.tutorialState.isTutorialActive && state.tutorialState.currentTutorial) {
      const tutorialDef = CONSTANTS.TUTORIALS[state.tutorialState.currentTutorial.id];
      if (!tutorialDef) return;
      const step = tutorialDef.steps[state.tutorialState.currentTutorial.step];
      if (step.spawnOrder) {
        const customerExists = state.customers.some(c => c.order === step.spawnOrder && c.type === 'tutorial');
        if (!customerExists) {
          const customer = {
            id: `customer_${state.customerIdCounter}`,
            name: "Tutorial Tom",
            order: step.spawnOrder,
            patience: 9999,
            maxPatience: 9999,
            type: "tutorial",
            active: true,
          };
          dispatch({ type: "ADD_CUSTOMER", customer });
        }
      }
    }
  }, [state.tutorialState.currentTutorial, state.tutorialState.isTutorialActive, dispatch, state.customers, state.customerIdCounter]);
};

const useTrendingDrinks = ({ state, dispatch }) => {
  const updateTrend = useCallback(() => {
    if (state.screen !== "game") return;

    const now = Date.now();
    // Check if trend should be updated
    if (now > state.trendingDrink.endTime) {
      // Trend expired, decide if a new one should start
      if (Math.random() < CONSTANTS.TREND_SYSTEM.CHANCE) {
        const discoveredDrinks = Object.keys(CONSTANTS.RECIPES)
          .filter(id => state.discoveredRecipes.includes(id))
          .map(id => CONSTANTS.RECIPES[id].result.id);
        
        if (discoveredDrinks.length > 0) {
          const newTrendId = discoveredDrinks[Math.floor(Math.random() * discoveredDrinks.length)];
          dispatch({
            type: "SET_TRENDING_DRINK",
            drink: {
              id: newTrendId,
              endTime: now + CONSTANTS.TREND_SYSTEM.DURATION,
            },
          });
        }
      } else {
        // No new trend, clear the old one
        if (state.trendingDrink.id) {
          dispatch({ type: "SET_TRENDING_DRINK", drink: { id: null, endTime: 0 } });
        }
      }
    }
  }, [state.screen, state.trendingDrink, state.discoveredRecipes, dispatch]);

  useEffect(() => {
    const interval = setInterval(updateTrend, CONSTANTS.TREND_SYSTEM.UPDATE_INTERVAL);
    return () => clearInterval(interval);
  }, [updateTrend]);
};

const useGameLoop = (props: { state: GameState; dispatch: any; audio: any; tutorial: any }) => {
  useLevelProgression(props);
  useEnvironmentalUpdates(props);
  useSessionManagement(props);
  useTutorialInit(props);
  useTutorialEffects(props);
  useTrendingDrinks(props);
};

export default useGameLoop;
